/**
 * <product-reviews>
 *
 * Client-side search, star-rating filter, sort and paginated (10-at-a-time) reveal for the product
 * reviews rendered by snippets/product-reviews.liquid. The review data arrives as a single JSON
 * blob (a <script type="application/json" data-reviews-json> child) rather than pre-rendered <li>
 * markup, because Shopify's Liquid `for` tag hard-caps at 50 iterations with no workaround — looping
 * over the review list in Liquid silently dropped everything past the 50th review (and skewed the
 * average/histogram, which summed only those 50 ratings while still dividing by the true total).
 * Outputting the whole list as one JSON value has no such cap, so this component builds every <li>
 * itself on connect, then shows/hides/reorders/highlights them exactly as before.
 *
 * Re-created from https://clarissakork.com/tischsets/tischset-koa/#product-ratings, with two
 * deliberate fixes over that reference: search and the star-rating filter combine (AND) instead of
 * one silently dropping the other when both are set, and clicking an already-active star row
 * clears the filter instead of doing nothing.
 */
if (!customElements.get('product-reviews')) {
  const STAR_PATH =
    'M8 0.5L10.0784 5.63932L15.6085 6.02786L11.3629 9.59268L12.7023 14.9721L8 12.036L3.29772 14.9721L4.63706 9.59268L0.391548 6.02786L5.92159 5.63932L8 0.5Z';

  // Mirrors snippets/star-rating.liquid exactly (same markup/classes), so the CSS built for the
  // server-rendered version keeps working unchanged.
  function buildStarRating(rating, size) {
    const span = document.createElement('span');
    span.className = `star-rating star-rating--${size || 'medium'}`;
    span.setAttribute('aria-hidden', 'true');
    let filled = Math.round(Number(rating) || 0);
    if (filled > 5) filled = 5;
    if (filled < 0) filled = 0;
    for (let i = 1; i <= 5; i += 1) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', `star-rating__star${i <= filled ? ' star-rating__star--filled' : ''}`);
      svg.setAttribute('viewBox', '0 0 16 16');
      svg.setAttribute('fill', 'none');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', STAR_PATH);
      path.setAttribute('fill', 'currentColor');
      svg.appendChild(path);
      span.appendChild(svg);
    }
    return span;
  }

  class ProductReviews extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-reviews-list]');
      this.searchInput = this.querySelector('[data-reviews-search]');
      this.sortSelect = this.querySelector('[data-reviews-sort]');
      this.loadMoreBtn = this.querySelector('[data-reviews-load-more]');
      this.countEl = this.querySelector('[data-reviews-count]');
      this.emptyEl = this.querySelector('[data-reviews-empty]');
      this.clearFilterBtn = this.querySelector('[data-reviews-clear-filter]');
      this.histogramRows = Array.from(this.querySelectorAll('[data-reviews-star-filter]'));

      this.items = this.buildItems();

      this.pageSize = 10;
      this.visibleCount = this.pageSize;
      this.activeRating = null;
      this.sortOrder = 'newest';
      this.searchTerm = '';

      this.onSearch = FoxTheme.utils.debounce(() => {
        this.searchTerm = this.searchInput.value.trim().toLowerCase();
        this.visibleCount = this.pageSize;
        this.update();
      }, 200);
      this.searchInput?.addEventListener('input', this.onSearch);

      this.onSortChange = () => {
        this.sortOrder = this.sortSelect.value;
        this.update();
      };
      this.sortSelect?.addEventListener('change', this.onSortChange);

      this.histogramRows.forEach((row) => {
        row.addEventListener('click', () => {
          const rating = row.dataset.reviewsStarFilter;
          this.activeRating = this.activeRating === rating ? null : rating;
          this.visibleCount = this.pageSize;
          this.update();
        });
      });

      this.onLoadMore = () => {
        this.visibleCount += this.pageSize;
        this.update();
      };
      this.loadMoreBtn?.addEventListener('click', this.onLoadMore);

      this.onClearFilter = () => {
        this.activeRating = null;
        this.searchTerm = '';
        if (this.searchInput) this.searchInput.value = '';
        this.visibleCount = this.pageSize;
        this.update();
      };
      this.clearFilterBtn?.addEventListener('click', this.onClearFilter);

      this.update();
    }

    disconnectedCallback() {
      this.searchInput?.removeEventListener('input', this.onSearch);
      this.sortSelect?.removeEventListener('change', this.onSortChange);
      this.loadMoreBtn?.removeEventListener('click', this.onLoadMore);
      this.clearFilterBtn?.removeEventListener('click', this.onClearFilter);
    }

    // A hyphen immediately before a digit survives dataset's camelCasing (data-month-1 becomes
    // dataset['month-1'], not dataset.month1) — same quirk as the star-label data attributes below.
    monthName(num) {
      return this.dataset[`month-${num}`] || '';
    }

    formatDate(isoDate) {
      const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate || '');
      if (!match) return '';
      const [, year, month, day] = match;
      const monthName = this.monthName(Number(month));
      return `${Number(day)}. ${monthName} ${year}`;
    }

    buildItems() {
      const script = this.querySelector('[data-reviews-json]');
      let reviews = [];
      try {
        reviews = script ? JSON.parse(script.textContent) : [];
      } catch (e) {
        reviews = [];
      }

      const verifiedIconTemplate = this.querySelector('[data-verified-icon]');
      const anonymousLabel = this.dataset.anonymousLabel || '';
      const verifiedLabel = this.dataset.verifiedLabel || '';

      return reviews.map((review) => {
        const author = (review.author || '').trim();
        const title = review.title || '';
        const body = review.body || '';
        const searchText = `${author} ${title} ${body}`.toLowerCase();

        const li = document.createElement('li');
        li.className = 'product-review';
        li.dataset.rating = String(review.rating || 0);
        li.dataset.date = review.date || '';
        li.dataset.search = searchText;

        const header = document.createElement('div');
        header.className = 'product-review__header flex items-center justify-between';
        const authorEl = document.createElement('span');
        authorEl.className = 'product-review__author';
        authorEl.textContent = author || anonymousLabel;
        const dateEl = document.createElement('span');
        dateEl.className = 'product-review__date text-subtext';
        dateEl.textContent = this.formatDate(review.date);
        header.append(authorEl, dateEl);
        li.appendChild(header);

        if (review.verified) {
          const verified = document.createElement('div');
          verified.className = 'product-review__verified text-subtext flex items-center gap-1';
          if (verifiedIconTemplate) verified.appendChild(verifiedIconTemplate.content.cloneNode(true));
          const label = document.createElement('span');
          label.textContent = verifiedLabel;
          verified.appendChild(label);
          li.appendChild(verified);
        }

        li.appendChild(buildStarRating(review.rating, 'small'));

        if (body) {
          const bodyEl = document.createElement('p');
          bodyEl.className = 'product-review__body rte';
          bodyEl.textContent = body;
          li.appendChild(bodyEl);
        }

        return li;
      });
    }

    get filteredAndSorted() {
      let items = this.items;
      if (this.activeRating) items = items.filter((li) => li.dataset.rating === this.activeRating);
      if (this.searchTerm) items = items.filter((li) => li.dataset.search.includes(this.searchTerm));

      return items.slice().sort((a, b) => {
        if (this.sortOrder === 'highest') {
          return Number(b.dataset.rating) - Number(a.dataset.rating) || b.dataset.date.localeCompare(a.dataset.date);
        }
        if (this.sortOrder === 'lowest') {
          return Number(a.dataset.rating) - Number(b.dataset.rating) || b.dataset.date.localeCompare(a.dataset.date);
        }
        return b.dataset.date.localeCompare(a.dataset.date);
      });
    }

    // Wraps the current search term in <mark> inside the review body, restoring the original text
    // first so repeated searches never nest highlights.
    highlight(li) {
      const body = li.querySelector('.product-review__body');
      if (!body) return;
      if (body.dataset.original === undefined) body.dataset.original = body.innerHTML;
      if (!this.searchTerm) {
        body.innerHTML = body.dataset.original;
        return;
      }
      const escaped = this.searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      body.innerHTML = body.dataset.original.replace(new RegExp(`(${escaped})`, 'ig'), '<mark>$1</mark>');
    }

    update() {
      const filtered = this.filteredAndSorted;
      const total = filtered.length;
      const shown = Math.min(this.visibleCount, total);

      this.items.forEach((li) => {
        li.hidden = true;
      });
      filtered.forEach((li, index) => {
        this.list.appendChild(li);
        li.hidden = index >= this.visibleCount;
        if (!li.hidden) this.highlight(li);
      });

      this.histogramRows.forEach((row) => {
        row.classList.toggle(
          'is-active',
          this.activeRating !== null && row.dataset.reviewsStarFilter === this.activeRating
        );
      });

      if (this.emptyEl) this.emptyEl.hidden = total !== 0;
      this.list.hidden = total === 0;

      if (this.clearFilterBtn) this.clearFilterBtn.hidden = !this.activeRating && !this.searchTerm;

      if (this.countEl) {
        if (total === 0) {
          this.countEl.textContent = '';
        } else {
          // A hyphen immediately before a digit survives dataset's camelCasing (data-star-label-5
          // becomes dataset['starLabel-5'], not dataset.starLabel5) — confirmed live.
          const starLabel = this.activeRating ? this.dataset[`starLabel-${this.activeRating}`] || '' : '';
          const template = this.activeRating ? this.dataset.countTemplateFiltered : this.dataset.countTemplate;
          this.countEl.textContent = template
            .replace('__FROM__', 1)
            .replace('__TO__', shown)
            .replace('__TOTAL__', total)
            .replace('__STARS__', starLabel);
        }
      }

      if (this.loadMoreBtn) {
        const remaining = total - shown;
        this.loadMoreBtn.hidden = remaining <= 0;
        if (remaining > 0) {
          this.loadMoreBtn.textContent = this.dataset.loadMoreTemplate.replace('__COUNT__', remaining);
        }
      }
    }
  }

  customElements.define('product-reviews', ProductReviews);
}

// Smooth-scrolls the title's "★★★★☆ (N)" link down to the section, respecting its
// scroll-margin-top (so the sticky header doesn't overlap it) instead of the browser's default
// instant, uncorrected anchor jump.
if (!window.productReviewsScrollBound) {
  window.productReviewsScrollBound = true;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href="#product-reviews"]');
    if (!link) return;
    const target = document.getElementById('product-reviews');
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    history.pushState(null, '', '#product-reviews');
  });
}
